import React, { useMemo, useRef, useState } from "react";
import "./navigation.css";
import JsonData from "../data/data.json";
import { newsPosts, donorRecords } from "../data/contentIndex";

const toText = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) return value.join(" ");
  if (typeof value === "object") return Object.values(value).join(" ");
  return String(value);
};

const makeEntry = ({ title, description, href, category, keywords }) => ({
  id: `${category}-${title}-${href}`.replace(/\s+/g, "-").toLowerCase(),
  title,
  description: description ? String(description) : "",
  href,
  category,
  keywords: keywords ? toText(keywords) : "",
});

const buildStaticIndex = () => {
  const pageEntries = [
    {
      title: "Home",
      description: JsonData?.Header?.paragraph,
      href: "/",
      category: "Pages",
      keywords: [JsonData?.Header?.title, JsonData?.Aim?.paragraph],
    },
    {
      title: "About",
      description: JsonData?.About?.paragraph,
      href: "/about",
      category: "Pages",
      keywords: ["about", "mission", "history"],
    },
    {
      title: "News",
      description: "Facebook news posts from the nursery.",
      href: "/news",
      category: "Pages",
      keywords: ["news", "facebook"],
    },
    {
      title: "Newsfeed",
      description: "Latest updates shared by the community.",
      href: "/newsfeed",
      category: "Pages",
      keywords: ["newsfeed", "updates"],
    },
    {
      title: "Videos",
      description: "Featured video uploads, livestreams, and highlights.",
      href: "/videos",
      category: "Pages",
      keywords: ["videos", "streaming"],
    },
    {
      title: "Donation Accounts",
      description: "Bank and payment account details.",
      href: "/accounts",
      category: "Pages",
      keywords: ["donation", "accounts", "bank"],
    },
    {
      title: "Donors",
      description: "Community of supporters and patrons.",
      href: "/donors",
      category: "Pages",
      keywords: ["donors", "supporters"],
    },
    {
      title: "Donation History",
      description: "Donation history and acknowledgments.",
      href: "/donation-history",
      category: "Pages",
      keywords: ["donation", "history"],
    },
    {
      title: "Novices",
      description: "Novice profiles and community members.",
      href: "/novices",
      category: "Pages",
      keywords: ["novices", "profiles"],
    },
    {
      title: "Gallery",
      description: "Photo gallery from the nursery.",
      href: "/gallery",
      category: "Pages",
      keywords: ["gallery", "photos"],
    },
    {
      title: "FAQ",
      description: "Frequently asked questions and answers.",
      href: "/faq",
      category: "Pages",
      keywords: ["faq", "questions"],
    },
    {
      title: "Contact",
      description: "Get in touch with the nursery.",
      href: "/contact",
      category: "Pages",
      keywords: ["contact", "address", "phone"],
    },
  ];

  const featureEntries =
    JsonData?.Features?.map((feature, index) =>
      makeEntry({
        title: `Feature ${index + 1}`,
        description: feature?.text,
        href: "/",
        category: "Highlights",
        keywords: feature?.icon,
      })
    ) ?? [];

  const aimEntries = [
    makeEntry({
      title: "Aim",
      description: JsonData?.Aim?.paragraph,
      href: "/",
      category: "Highlights",
      keywords: JsonData?.Aim?.Why,
    }),
  ];

  const newsEntries =
    newsPosts?.map((post) =>
      makeEntry({
        title: post.title,
        description: post.description,
        href: "/news",
        category: "News",
        keywords: post.title,
      })
    ) ?? [];

  const donorEntries =
    donorRecords?.map((donor) =>
      makeEntry({
        title: donor.name,
        description: `${donor.address} ${donor.phone}`,
        href: "/donors",
        category: "Donors",
        keywords: donor.name,
      })
    ) ?? [];

  return [
    ...pageEntries.map(makeEntry),
    ...featureEntries,
    ...aimEntries,
    ...newsEntries,
    ...donorEntries,
  ];
};

const normalize = (value) => value.toLowerCase();
const truncate = (value, max = 160) => {
  if (!value) return "";
  if (value.length <= max) return value;
  return `${value.slice(0, max).trim()}…`;
};

export const Navigation = (props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const index = useMemo(buildStaticIndex, []);

  const results = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const tokens = normalize(trimmed).split(/\s+/).filter(Boolean);
    return index
      .map((entry) => {
        const haystack = normalize(
          `${entry.title} ${entry.description} ${entry.category} ${entry.keywords}`
        );
        const matches = tokens.filter((token) => haystack.includes(token));
        return { ...entry, score: matches.length };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  }, [index, query]);

  const openSearch = () => {
    setIsSearchOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setQuery("");
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (key) => {
    setOpenDropdown((current) => (current === key ? null : key));
  };

  return (
    <nav id="menu" className="navbar navbar-default navbar-fixed-top">
      <div className="container">
        <div className="navbar-header">
          <button
            type="button"
            className="navbar-toggle collapsed"
            aria-label="Toggle navigation"
            aria-expanded={isMenuOpen}
            aria-controls="bs-example-navbar-collapse-1"
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            {" "}
            <span className="sr-only">Toggle navigation</span>{" "}
            <span className="icon-bar"></span>{" "}
            <span className="icon-bar"></span>{" "}
            <span className="icon-bar"></span>{" "}
          </button>
          <a className="navbar-brand page-scroll" href="#page-top">
            <img
              src="/img/icon.png"
              alt="Sasananursery Logo"
              className="navigation-logo"
            />
          </a>
        </div>

        <div
          className={`collapse navbar-collapse${isMenuOpen ? " is-open" : ""}`}
          id="bs-example-navbar-collapse-1"
        >
          <ul className="nav navbar-nav navbar-right">
            <li>
              <a href="/" className="page-scroll" onClick={closeMenu}>
                ပင်မစာမျက်နှာ
              </a>
            </li>
            <li>
              <a href="/about" className="page-scroll" onClick={closeMenu}>
                အကြောင်းအရာ
              </a>
            </li>

            <li className={`dropdown${openDropdown === "donations" ? " is-open" : ""}`}>
              <a
                href="#!"
                onClick={(event) => {
                  event.preventDefault();
                  toggleDropdown("donations");
                }}
                role="button"
                className="page-scroll dropdown-toggle dropdown-link-button"
                aria-haspopup="true"
                aria-expanded={openDropdown === "donations"}
              >
                သာသနာ့အလှူ
                <img
                  src="/img/icons/dropdown.png"
                  alt=""
                  className="dropdown-icon"
                  aria-hidden="true"
                />
              </a>
              <ul className="dropdown-menu">
                <li>
                  <a href="/accounts" className="page-scroll" onClick={closeMenu}>
                    လှူဒါန်းရန်
                  </a>
                </li>
                <li>
                  <a href="/donors" className="page-scroll" onClick={closeMenu}>
                    အလှူရှင်စာရင်း
                  </a>
                </li>
                <li>
                  <a href="/donation-history" className="page-scroll" onClick={closeMenu}>
                    အလှူမှတ်တမ်း
                  </a>
                </li>
                <li>
                  <a
                    href=" https://v0-ssn-donation-managment-system.vercel.app"
                    className="page-scroll"
                    onClick={closeMenu}
                  >
                    ယနေ့အလှူ
                  </a>
                </li>
              </ul>
            </li>

            <li className={`dropdown${openDropdown === "news" ? " is-open" : ""}`}>
              <a
                href="#!"
                onClick={(event) => {
                  event.preventDefault();
                  toggleDropdown("news");
                }}
                role="button"
                className="page-scroll dropdown-toggle dropdown-link-button"
                aria-haspopup="true"
                aria-expanded={openDropdown === "news"}
              >
                သတင်းများ
                <img
                  src="/img/icons/dropdown.png"
                  alt=""
                  className="dropdown-icon"
                  aria-hidden="true"
                />
              </a>
              <ul className="dropdown-menu">
                <li>
                  <a href="/news" className="page-scroll" onClick={closeMenu}>
                    Facebook သတင်းများ
                  </a>
                </li>
                <li>
                  <a href="/newsfeed" className="page-scroll" onClick={closeMenu}>
                    ကျောင်းတိုက်သတင်းများ
                  </a>
                </li>
                <li>
                  <a href="/videos" className="page-scroll" onClick={closeMenu}>
                    ရုပ်သံဖိုင်များ
                  </a>
                </li>
              </ul>
            </li>

            <li className={`dropdown${openDropdown === "profiles" ? " is-open" : ""}`}>
              <a
                href="#!"
                onClick={(event) => {
                  event.preventDefault();
                  toggleDropdown("profiles");
                }}
                role="button"
                className="page-scroll dropdown-toggle dropdown-link-button"
                aria-haspopup="true"
                aria-expanded={openDropdown === "profiles"}
              >
                ဓာတ်ပုံများ
                <img
                  src="/img/icons/dropdown.png"
                  alt=""
                  className="dropdown-icon"
                  aria-hidden="true"
                />
              </a>
              <ul className="dropdown-menu">
                <li>
                  <a href="/novices" className="page-scroll" onClick={closeMenu}>
                    သာမဏေများ
                  </a>
                </li>
                <li>
                  <a href="/gallery" className="page-scroll" onClick={closeMenu}>
                    ကျောင်းတိုက်လှုပ်ရှားမှုများ
                  </a>
                </li>
              </ul>
            </li>

            <li>
              <a href="/faq" className="page-scroll" onClick={closeMenu}>
                အမေးအဖြေများ
              </a>
            </li>

            <li>
              <a href="/contact" className="page-scroll" onClick={closeMenu}>
                ဆက်သွယ်ရန်
              </a>
            </li>
            <li>
              <a
                href="https://uat-admin.sasananurserymonastery.com/admin/login"
                className="page-scroll"
                onClick={closeMenu}
                style={{
                  display: "inline-block",
                  padding: "10px 16px",
                  backgroundColor: "#5472D2",
                  color: "#F0F2F5",
                  borderRadius: "12px",
                  textDecoration: "none",
                }}
              >
                အကောင့်
              </a>
            </li>
            <li className="nav-search">
              <button
                type="button"
                className="nav-search-button"
                onClick={() => (isSearchOpen ? closeSearch() : openSearch())}
                aria-label="Search site content"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    d="M11 3a8 8 0 1 1 0 16 8 8 0 0 1 0-16Zm0 2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm7.71 12.29 3.7 3.7a1 1 0 0 1-1.42 1.42l-3.7-3.7a1 1 0 0 1 1.42-1.42Z"
                    fill="currentColor"
                  />
                </svg>
                <span className="sr-only">Toggle search</span>
              </button>
            </li>
          </ul>
        </div>
      </div>
      {isSearchOpen && (
        <div className="nav-search-panel">
          <div className="nav-search-panel__inner">
            <div className="nav-search-panel__header">
              <span>Search</span>
              <button type="button" onClick={closeSearch} aria-label="Close search">
                ×
              </button>
            </div>
            <input
              ref={inputRef}
              type="search"
              placeholder="Search pages, news, donors..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  closeSearch();
                }
              }}
            />
            {query && results.length === 0 && (
              <p className="nav-search-panel__empty">No matches found.</p>
            )}
            {results.length > 0 && (
              <div className="nav-search-panel__results">
                {results.map((result) => (
                  <a
                    key={result.id}
                    href={result.href}
                    className="nav-search-panel__result"
                    onClick={closeSearch}
                  >
                    <span className="nav-search-panel__meta">{result.category}</span>
                    <strong>{result.title}</strong>
                    <span>{truncate(result.description)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
